// ═══════════════════════════════════════════════════════════════
// Bulk Import — Shared spreadsheet parsing for contractors & invoices
// Used by Contractors page, Invoices page, and PackageProcessor
// ═══════════════════════════════════════════════════════════════

import * as XLSX from 'xlsx';

// ── Column maps: header text patterns → field names ──────────

const CONTRACTOR_COLUMN_MAP: Record<string, string> = {
  'company name': 'companyName',
  'company': 'companyName',
  'vendor': 'companyName',
  'contractor': 'companyName',
  'contractor name': 'companyName',
  'contract number': 'contractNumber',
  'contract #': 'contractNumber',
  'contract no': 'contractNumber',
  'contact name': 'contactName',
  'contact': 'contactName',
  'contact person': 'contactName',
  'phone': 'contactPhone',
  'contact phone': 'contactPhone',
  'email': 'contactEmail',
  'contact email': 'contactEmail',
  'contract amount': 'contractAmount',
  'amount': 'contractAmount',
  'contract ceiling': 'contractCeiling',
  'ceiling': 'contractCeiling',
  'category': 'vendorCategory',
  'vendor category': 'vendorCategory',
  'department': 'managingDepartment',
  'managing department': 'managingDepartment',
  'city poc': 'cityPOC',
  'poc': 'cityPOC',
  'city poc email': 'cityPOCEmail',
  'poc email': 'cityPOCEmail',
  'status': 'status',
  'prime contractor': 'primeContractorName',
  'prime': 'primeContractorName',
  'parent': 'primeContractorName',
  'notes': 'notes',
};

const INVOICE_COLUMN_MAP: Record<string, string> = {
  'invoice number': 'invoiceNumber',
  'invoice #': 'invoiceNumber',
  'inv #': 'invoiceNumber',
  'inv no': 'invoiceNumber',
  'invoice no': 'invoiceNumber',
  'contractor': 'contractorName',
  'vendor': 'contractorName',
  'company': 'contractorName',
  'company name': 'contractorName',
  'invoice date': 'invoiceDate',
  'date': 'invoiceDate',
  'total amount': 'totalAmount',
  'amount': 'totalAmount',
  'total': 'totalAmount',
  'period start': 'periodStart',
  'start date': 'periodStart',
  'from': 'periodStart',
  'period end': 'periodEnd',
  'end date': 'periodEnd',
  'to': 'periodEnd',
  'invoice type': 'invoiceType',
  'type': 'invoiceType',
  'description': 'notes',
  'notes': 'notes',
};

// ── Types ────────────────────────────────────────────────────

export interface ContractorRow {
  rowNum: number;
  companyName: string;
  contractNumber: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  contractAmount: string;
  contractCeiling: string;
  vendorCategory: string;
  managingDepartment: string;
  cityPOC: string;
  cityPOCEmail: string;
  status: string;
  primeContractorName: string;
  notes: string;
  // Validation
  valid: boolean;
  errors: string[];
  duplicate: boolean;
  existingId: string; // record_id of existing contractor (for upsert)
}

export interface InvoiceRow {
  rowNum: number;
  invoiceNumber: string;
  contractorName: string;
  invoiceDate: string;
  totalAmount: number;
  periodStart: string;
  periodEnd: string;
  invoiceType: string;
  notes: string;
  // Resolution
  matchedContractorId: string;
  matchedContractorName: string;
  // Validation
  valid: boolean;
  errors: string[];
  duplicate: boolean;
  existingId: string; // record_id of existing invoice (for upsert)
}

export interface RowError {
  row: number;
  field: string;
  message: string;
}

// ── Helpers ──────────────────────────────────────────────────

function readFile(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(new Uint8Array(e.target?.result as ArrayBuffer));
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

function parseCurrency(val: unknown): number {
  if (typeof val === 'number') return val;
  const s = String(val ?? '').replace(/[$,\s]/g, '');
  return parseFloat(s) || 0;
}

function normalizeHeader(h: string): string {
  return h.toLowerCase().replace(/[^a-z0-9 #]/g, '').trim();
}

function mapHeaders(rawHeaders: string[], columnMap: Record<string, string>): Map<number, string> {
  const mapping = new Map<number, string>();
  rawHeaders.forEach((raw, idx) => {
    const norm = normalizeHeader(raw);
    if (columnMap[norm]) {
      mapping.set(idx, columnMap[norm]);
    }
  });
  return mapping;
}

function normalizeContractorName(name: string): string {
  return name.toLowerCase()
    .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, '')
    .replace(/\b(inc|llc|lc|corp|corporation|ltd|co|company|incorporated|environmental|env|services|service|construction|contractors?|contracting|group|enterprises?|solutions|management|systems|industries|industry|international|national|associates?|partners?|holdings?|resources?)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// ── Spreadsheet type detection ───────────────────────────────

export function detectSpreadsheetType(headers: string[]): 'contractor_register' | 'invoice_register' | null {
  const h = headers.map(normalizeHeader).join(' ');

  // Invoice register: must have invoice number + amount
  if ((h.includes('invoice number') || h.includes('invoice #') || h.includes('inv #') || h.includes('inv no')) &&
      (h.includes('amount') || h.includes('total'))) {
    return 'invoice_register';
  }

  // Contractor register: must have company/vendor + contract info or contact info
  if ((h.includes('company name') || h.includes('contractor') || h.includes('vendor')) &&
      (h.includes('contract') || h.includes('contact') || h.includes('phone') || h.includes('email'))) {
    return 'contractor_register';
  }

  return null;
}

// ── Contractor spreadsheet parser ────────────────────────────

export async function parseContractorSpreadsheet(
  file: File,
  existingContractors?: Array<{ id: number | string; companyName: string }>,
): Promise<{ rows: ContractorRow[]; errors: RowError[]; unmapped: string[] }> {
  const data = await readFile(file);
  const wb = XLSX.read(data, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, raw: false, defval: '' });

  if (raw.length < 2) return { rows: [], errors: [{ row: 0, field: '', message: 'Spreadsheet is empty or has no data rows' }], unmapped: [] };

  const headers = raw[0].map(String);
  const colMap = mapHeaders(headers, CONTRACTOR_COLUMN_MAP);
  const unmapped = headers.filter((_, i) => !colMap.has(i) && headers[i].trim());

  // Build lookup: normalized name → existing record ID
  const existingByName = new Map<string, string>();
  (existingContractors ?? []).forEach(c => {
    existingByName.set(normalizeContractorName(c.companyName), String(c.id));
  });
  const seenNames = new Set<string>();
  const rows: ContractorRow[] = [];
  const errors: RowError[] = [];

  for (let r = 1; r < raw.length; r++) {
    const cells = raw[r];
    if (!cells || cells.every(c => !String(c).trim())) continue; // skip blank rows

    const record: Record<string, string> = {};
    colMap.forEach((field, colIdx) => {
      record[field] = String(cells[colIdx] ?? '').trim();
    });

    const rowErrors: string[] = [];
    if (!record.companyName) rowErrors.push('Company Name is required');

    const normName = normalizeContractorName(record.companyName || '');
    const existingId = existingByName.get(normName) || '';
    const isDuplicateInFile = seenNames.has(normName);
    if (isDuplicateInFile) rowErrors.push('Duplicate within file');
    if (normName) seenNames.add(normName);

    // Normalize status
    const status = (record.status || 'active').toLowerCase();
    const validStatuses = ['active', 'suspended', 'completed', 'inactive'];
    if (!validStatuses.includes(status)) rowErrors.push(`Invalid status: "${record.status}"`);

    rows.push({
      rowNum: r + 1,
      companyName: record.companyName || '',
      contractNumber: record.contractNumber || '',
      contactName: record.contactName || '',
      contactPhone: record.contactPhone || '',
      contactEmail: record.contactEmail || '',
      contractAmount: record.contractAmount || '',
      contractCeiling: record.contractCeiling || '',
      vendorCategory: record.vendorCategory || '',
      managingDepartment: record.managingDepartment || '',
      cityPOC: record.cityPOC || '',
      cityPOCEmail: record.cityPOCEmail || '',
      status: validStatuses.includes(status) ? status : 'active',
      primeContractorName: record.primeContractorName || '',
      notes: record.notes || '',
      valid: rowErrors.length === 0,
      errors: rowErrors,
      duplicate: !!existingId || isDuplicateInFile,
      existingId,
    });

    rowErrors.forEach(msg => errors.push({ row: r + 1, field: '', message: msg }));
  }

  return { rows, errors, unmapped };
}

// ── Invoice spreadsheet parser ───────────────────────────────

export async function parseInvoiceSpreadsheet(
  file: File,
  existingContractors: Array<{ id: number | string; companyName: string }>,
  existingInvoices?: Array<{ id?: number | string; invoiceNumber: string }>,
): Promise<{ rows: InvoiceRow[]; errors: RowError[]; unmapped: string[] }> {
  const data = await readFile(file);
  const wb = XLSX.read(data, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, raw: false, defval: '' });

  if (raw.length < 2) return { rows: [], errors: [{ row: 0, field: '', message: 'Spreadsheet is empty or has no data rows' }], unmapped: [] };

  const headers = raw[0].map(String);
  const colMap = mapHeaders(headers, INVOICE_COLUMN_MAP);
  const unmapped = headers.filter((_, i) => !colMap.has(i) && headers[i].trim());

  // Build lookup: invoice number → existing record ID
  const existingByInvNum = new Map<string, string>();
  (existingInvoices ?? []).forEach(i => {
    existingByInvNum.set(i.invoiceNumber.toLowerCase().trim(), String(i.id ?? ''));
  });
  const seenInvNums = new Set<string>();
  const rows: InvoiceRow[] = [];
  const errors: RowError[] = [];

  for (let r = 1; r < raw.length; r++) {
    const cells = raw[r];
    if (!cells || cells.every(c => !String(c).trim())) continue;

    const record: Record<string, string> = {};
    colMap.forEach((field, colIdx) => {
      record[field] = String(cells[colIdx] ?? '').trim();
    });

    const rowErrors: string[] = [];
    if (!record.invoiceNumber) rowErrors.push('Invoice Number is required');
    const totalAmount = parseCurrency(record.totalAmount);
    if (!record.totalAmount && totalAmount === 0) rowErrors.push('Total Amount is required');

    // Dedup
    const invKey = (record.invoiceNumber || '').toLowerCase().trim();
    const existingId = existingByInvNum.get(invKey) || '';
    const isDuplicateInFile = seenInvNums.has(invKey);
    if (isDuplicateInFile) rowErrors.push('Duplicate within file');
    if (invKey) seenInvNums.add(invKey);

    // Fuzzy-match contractor (unmatched contractors will be auto-created on import)
    let matchedContractorId = '';
    let matchedContractorName = '';
    const vendorName = record.contractorName || '';
    if (vendorName) {
      const normInput = normalizeContractorName(vendorName);
      const match = existingContractors.find(c => {
        const normC = normalizeContractorName(c.companyName);
        return normC === normInput || normC.includes(normInput) || normInput.includes(normC);
      });
      if (match) {
        matchedContractorId = String(match.id);
        matchedContractorName = match.companyName;
      }
      // No error for unmatched — contractor will be auto-created during import
    }

    // Normalize invoice type
    const rawType = (record.invoiceType || '').toLowerCase().replace(/[^a-z_]/g, '');
    const validTypes = ['debris', 'non_debris_contractor', 'force_account_labor', 'force_account_equipment'];
    const invoiceType = validTypes.includes(rawType) ? rawType
      : rawType.includes('debris') ? 'debris'
      : rawType.includes('service') || rawType.includes('non') ? 'non_debris_contractor'
      : '';

    rows.push({
      rowNum: r + 1,
      invoiceNumber: record.invoiceNumber || '',
      contractorName: vendorName,
      invoiceDate: record.invoiceDate || '',
      totalAmount,
      periodStart: record.periodStart || '',
      periodEnd: record.periodEnd || '',
      invoiceType,
      notes: record.notes || '',
      matchedContractorId,
      matchedContractorName,
      valid: rowErrors.length === 0,
      errors: rowErrors,
      duplicate: !!existingId || isDuplicateInFile,
      existingId,
    });

    rowErrors.forEach(msg => errors.push({ row: r + 1, field: '', message: msg }));
  }

  return { rows, errors, unmapped };
}

// ── Template generators ──────────────────────────────────────

export function generateContractorTemplate(): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  const headers = [
    'Company Name', 'Contract Number', 'Contact Name', 'Phone', 'Email',
    'Contract Amount', 'Contract Ceiling', 'Category', 'Managing Department',
    'City POC', 'POC Email', 'Prime Contractor', 'Status', 'Notes',
  ];
  const example = [
    'Himes Electric LLC', 'C-2024-0042', 'John Himes', '813-555-0100', 'john@himeselectric.com',
    '150000', '200000', 'Electrical', 'Public Works', 'Jane Smith', 'jsmith@tampa.gov',
    '', 'active', 'Licensed electrical contractor',
  ];
  const ws = XLSX.utils.aoa_to_sheet([headers, example]);
  // Set column widths
  ws['!cols'] = headers.map(h => ({ wch: Math.max(h.length + 4, 14) }));
  XLSX.utils.book_append_sheet(wb, ws, 'Contractors');
  return wb;
}

export function generateInvoiceTemplate(): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  const headers = [
    'Invoice Number', 'Contractor', 'Invoice Date', 'Total Amount',
    'Period Start', 'Period End', 'Type', 'Notes',
  ];
  const example = [
    'INV-2024-001', 'Himes Electric LLC', '2024-10-15', '45000.00',
    '2024-10-01', '2024-10-31', 'non_debris_contractor', 'Electrical restoration services',
  ];
  const ws = XLSX.utils.aoa_to_sheet([headers, example]);
  ws['!cols'] = headers.map(h => ({ wch: Math.max(h.length + 4, 14) }));
  XLSX.utils.book_append_sheet(wb, ws, 'Invoices');
  return wb;
}

export function downloadWorkbook(wb: XLSX.WorkBook, filename: string) {
  XLSX.writeFile(wb, filename);
}
